import { Box } from "@chakra-ui/react";
import { colors } from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import { ReactNode } from "react";

/**
 * 비용 안내 리드 퍼널 공용 UI 아톰.
 * 디자인(design_handoff_funeral_cost_lead_funnel)을 GOI 토큰으로 매핑했다.
 */

/** 형광펜 강조. `::selection` 같은 pseudo 가 아니라 배경 그라디언트라 styled 없이 처리된다. */
export function Highlight({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        fontWeight: 700,
        color: colors.gray900,
        background: `linear-gradient(to top, ${colors.orange400} 55%, transparent 55%)`,
        padding: "0 2px",
      }}
    >
      {children}
    </span>
  );
}

/** 굵게 강조. 형광펜(`Highlight`)보다 약한 단계. */
export function Strong({ children }: { children: ReactNode }) {
  return <span style={{ fontWeight: 700, color: colors.gray900 }}>{children}</span>;
}

/** 오렌지 강조 (H1 안의 핵심어). */
export function Accent({ children }: { children: ReactNode }) {
  return <span style={{ color: colors.orange650 }}>{children}</span>;
}

/** 본문 카드. 회색 면 + 1px 보더. 디자인 전반의 기본 카드. */
export function FunnelCard({
  children,
  padding = "20px",
}: {
  children: ReactNode;
  padding?: string;
}) {
  return (
    <Box
      width="100%"
      background={colors.gray50}
      border={`1px solid ${colors.gray200}`}
      borderRadius="16px"
      padding={padding}
    >
      {children}
    </Box>
  );
}

/** 참고 박스. 배경 없이 보더만. */
export function FunnelNoteBox({
  label,
  labelClassName = "caption_12_b",
  children,
}: {
  label?: string;
  /**
   * 라벨 타이포. 기본은 "참고" 류 캡션이고, 라벨이 캡션이 아니라 **내용의 제목** 역할일 때
   * (무빈소 안내처럼) 한 단계 키워 넘긴다.
   */
  labelClassName?: string;
  children: ReactNode;
}) {
  return (
    <Box
      width="100%"
      border={`1px solid ${colors.gray200}`}
      borderRadius="14px"
      padding="14px 16px"
    >
      {label && (
        <>
          <Box className={labelClassName} color={colors.gray500}>
            {label}
          </Box>
          <Box height="6px" />
        </>
      )}
      <Box className="body_14_r" color={colors.gray800}>
        {children}
      </Box>
    </Box>
  );
}

/** 카드 안 얇은 구분선. */
export function FunnelDivider({ color = colors.gray200 }: { color?: string }) {
  return <Box width="100%" height="1px" background={color} />;
}

/**
 * 스텝 소제목 + 번호 H1. Step 3·4 가 공유한다.
 * (`예상 조문객 수`에 따라 / `1` 적절한 빈소 평수를 확인하세요)
 */
export function NumberedStepHeading({
  eyebrowStrong,
  eyebrowRest,
  index,
  title,
}: {
  eyebrowStrong: string;
  eyebrowRest: string;
  index: number;
  title: string;
}) {
  return (
    <BaseStack width="100%" spacing={6}>
      {/* 두 색의 대비는 유지하면서 각각 한 단계 진하게(gray500->600, gray400->500),
          크기도 한 단계 위(subtitle_14 -> subtitle_16). */}
      <Box className="subtitle_16_b" color={colors.gray500}>
        <span style={{ color: colors.gray600 }}>{eyebrowStrong}</span>
        {eyebrowRest}
      </Box>
      <BaseStack direction="horizontal" spacing={9} alignItems="baseline">
        <Box
          as="span"
          className="head_28_eb"
          color={colors.orange650}
          css={{ lineHeight: 1, letterSpacing: "-0.03em" }}
        >
          {index}
        </Box>
        <Box as="h2" className="subtitle_21_b" color={colors.gray900}>
          {title}
        </Box>
      </BaseStack>
    </BaseStack>
  );
}
